<script setup>
import { computed, ref, inject, watch, onBeforeUnmount } from 'vue'

import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import StreamService from '@/services/StreamService'
import FormButton from '../Atoms/FormButton.vue'
import FormCheckbox from '../Atoms/FormCheckbox.vue'
import FormRadio from '../Atoms/FormRadio.vue'
import IconBase from '../icon/IconBase.vue'
import IconSearch from '../icon/IconSearch.vue'
import IconCross from '../icon/IconCross.vue'
import IconChevronRightFilled from '../icon/IconChevronRightFilled.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconChevronRight from '../icon/IconChevronRight.vue'
import IconChevronLeft from '../icon/IconChevronLeft.vue'
import IconFolderFilled from '../icon/IconFolderFilled.vue'
import IconFilm from '../icon/IconFilm.vue'
import Badge from '../Badge.vue'
import FormInput from '../Atoms/FormInput.vue'
import Spinner from './Spinner.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import PaginatedList from './PaginatedList.vue'
import IconSearchEmpty from '../icon/IconSearchEmpty.vue'
import { bytes, duration } from '../../filtersNew'
import { sortStreams } from '../../utils'

const store = useStore()
const { notify } = useNotification()
const playlistContentSelection = inject('playlistContentSelection', null)

// Props
const props = defineProps({
	multiview: {
		type: Boolean,
		default: false,
	},
	source: {
		type: String,
		default: 'playout',
	},
	variant: {
		type: String,
		default: 'default',
	},
	itemsPerPage: {
		type: Number,
		default: 5,
	},
	itemsPerPageOptions: {
		type: Array,
		default: () => [5, 10, 20],
	},
	showItemsPerPageSelector: {
		type: Boolean,
		default: false,
	},
})

// Emits
const emit = defineEmits({
	'add-vod': () => true,
})

// Refs
const loadFiles = ref(false)
const activeFolder = ref(null)
// Sub-folder drill-down inside the open folder (visual grouping only)
const activeSubfolder = ref(null)
const search = ref('')
const selectedVod = ref(null)
// Computed
const hasVodFiles = computed(() => store.getters['Streams/hasVodFiles'])
const vodStreams = computed(() => store.getters['Streams/vodStreams'])
const vodFiles = computed(() => store.getters['Streams/vodFiles'])
const shouldShowCheckbox = computed(() => props.source === 'playlist')
const shouldShowRadio = computed(() => props.source === 'dynamic')
// Folder checkboxes and "Select all" need the playlist modal's bulk selection API
const hasBulkSelection = computed(() => shouldShowCheckbox.value && !!playlistContentSelection)
const isFolderOpen = computed(() => !!activeFolder.value)

const hasSearchQuery = computed(() => search.value.trim().length > 0)

const searchPlaceholder = computed(() =>
	isFolderOpen.value ? 'Search videos' : 'Search folders',
)

const vodFoldersFiltered = computed(() => {
	const query = search.value.trim().toLowerCase()
	const vodFolders = _.cloneDeep(
		vodStreams.value.filter(
			(stream) =>
				stream.totalFiles > 0 &&
				stream.name.toLowerCase().includes(query),
		),
	)
	return vodFolders.sort(
		(a, b) => new Date(b.creationTime) - new Date(a.creationTime),
	)
})

const foldersForList = computed(() => {
	if (isFolderOpen.value && activeFolder.value) {
		return [activeFolder.value]
	}
	return vodFoldersFiltered.value
})

const folderSubfolders = computed(() => {
	if (!isFolderOpen.value || activeFolder.value?.liveParent) return []
	return store.getters['Streams/vodSubfolders'] || []
})
const folderSubfolderIds = computed(() => new Set(folderSubfolders.value.map((s) => s._id)))

const vodFilesInFolderFiltered = computed(() => {
	const query = search.value.trim().toLowerCase()
	let files = vodFiles.value || []
	if (!isFolderOpen.value) return files
	// Scope to the drill-down level: sub-folder shows its own videos, the
	// folder root shows untagged videos (tags to deleted sub-folders count
	// as untagged) plus sub-folder rows rendered separately.
	if (activeSubfolder.value) {
		files = files.filter((f) => f.subfolder === activeSubfolder.value._id)
	} else {
		files = files.filter((f) => !f.subfolder || !folderSubfolderIds.value.has(f.subfolder))
	}
	if (!query) return files
	return files.filter((file) =>
		file.fileName?.toLowerCase().includes(query),
	)
})

// Rows for the open folder: sub-folders first (at root level), then videos.
const folderContentItems = computed(() => {
	if (!isFolderOpen.value) return []
	const files = vodFilesInFolderFiltered.value
	if (activeSubfolder.value) return files
	const query = search.value.trim().toLowerCase()
	const allFiles = vodFiles.value || []
	const subfolderRows = folderSubfolders.value
		.filter((s) => !query || s.name?.toLowerCase().includes(query))
		.map((s) => ({
			_id: s._id,
			name: s.name,
			__subfolder: true,
			totalFiles: allFiles.filter((f) => f.subfolder === s._id).length,
		}))
	return [...subfolderRows, ...files]
})

// "Select all" inside the open folder: every video visible at this level (all
// pages, search applied). At the folder root, visible sub-folder rows count as
// their videos, matching the sub-folder checkbox.
const selectAllScope = computed(() => {
	if (!hasBulkSelection.value || !isFolderOpen.value || loadFiles.value) return []
	if (activeSubfolder.value) return _.uniqBy(vodFilesInFolderFiltered.value, 'id')
	// Same order as the list: sub-folder rows first, then root videos
	const files = folderContentItems.value
		.filter((item) => item.__subfolder)
		.flatMap((row) => subfolderFiles(row._id))
	return _.uniqBy([...files, ...vodFilesInFolderFiltered.value], 'id')
})

const selectAllState = computed(() =>
	selectAllScope.value.length
		? playlistContentSelection.getState(selectAllScope.value, 'vod')
		: 'none',
)

const showSearchEmptyState = computed(() => {
	if (!hasSearchQuery.value) return false
	if (isFolderOpen.value) {
		if (loadFiles.value) return false
		return folderContentItems.value.length === 0
	}
	return vodFoldersFiltered.value.length === 0
})

const listClasses = computed(() => {
	const variantClass =
		props.variant === 'modern'
			? 'grid grid-cols-1 gap-2'
			: 'flex flex-col divide-y divide-surface-3 max-h-[400px]'
	const basicClass = 'overflow-y-auto overflow-x-hidden'
	return `${basicClass} ${variantClass}`
})

const subListClasses = computed(() => {
	const variantClass = props.variant === 'modern' ? 'grid grid-cols-1 gap-2' : ''
	return variantClass
})

const subListItemClasses = computed(() => {
	const variantClass =
		props.variant === 'modern'
			? 'bg-surface-3 hover:bg-surface-4 gap-2 !p-2 !rounded-md !border !border-transparent text-surface-text-2'
			: 'text-surface-7'
	const basicClass = 'hover:bg-surface-3 min-h-9 flex items-center'
	const activeStateClass =
		props.variant === 'modern'
			? 'has-[input:checked]:!border-main has-[input:checked]:bg-main-transparent'
			: ''
	return `${basicClass} ${variantClass} ${activeStateClass}`
})

const folderItemClasses = (folder) => {
	const variantClass =
		props.variant === 'modern'
			? `gap-2 !p-2 !rounded-md !border !border-transparent text-surface-text-2 ${shouldShowFilesInFolder(folder) ? '' : 'h-12 bg-surface-3 hover:bg-surface-4'} ${activeFolder.value && !shouldShowFilesInFolder(folder) ? 'hidden' : ''}`
			: 'py-1 pr-2 text-surface-7'
	return variantClass
}

// Methods
const clearSearch = () => {
	search.value = ''
}

const shouldShowAddVodFileButon = () => {
	return (
		props.source === 'playout' ||
		props.source === 'dynamic' ||
		props.source === 'mediaflow' ||
		props.source === 'paywall' ||
		props.source === 'sites'
	)
}

const getFiles = async (folder) => {
	if (folder?._id !== activeFolder.value?._id) {
		loadFiles.value = true
		activeFolder.value = folder
		activeSubfolder.value = null
		await Promise.all([
			store.dispatch('Streams/getVodFilesByVodId', folder._id),
			store.dispatch('Streams/getVodSubfolders', folder.liveParent ? null : folder._id),
		])
		loadFiles.value = false
		// The folder is fully loaded now, so its checkbox state is known when going back
		if (hasBulkSelection.value && folder._id === activeFolder.value?._id) {
			folderFilesCache.value = { ...folderFilesCache.value, [folder._id]: vodFiles.value }
		}
	} else if (activeSubfolder.value) {
		// Back from a sub-folder lands on the folder root, not the folder list
		activeSubfolder.value = null
	} else {
		activeFolder.value = null
	}
}

const openSubfolder = (subfolderRow) => {
	activeSubfolder.value = subfolderRow
}

// "Select this folder": in checkbox (playlist) mode a sub-folder can be
// selected as a unit — it simply toggles all of its videos.
const subfolderFiles = (subfolderId) => (vodFiles.value || []).filter((f) => f.subfolder === subfolderId)
const subfolderState = (subfolderRow) => {
	if (!hasBulkSelection.value) return 'none'
	return playlistContentSelection.getState(subfolderFiles(subfolderRow._id), 'vod')
}

// Bulk checkboxes act on the checked value the user just set (not on state read
// later), and are remounted when nothing could be applied so the native box
// can't stay ticked with nothing selected.
const bulkCheckboxKey = ref(0)
const applyBulkSelection = (files, select) => {
	if (!files?.length) {
		bulkCheckboxKey.value++
		return
	}
	playlistContentSelection.setSelected(files, 'vod', select)
}

const toggleSubfolder = (subfolderRow, select) => {
	if (!hasBulkSelection.value) return
	applyBulkSelection(subfolderFiles(subfolderRow._id), select)
}

const toggleSelectAll = (select) => {
	if (!hasBulkSelection.value) return
	applyBulkSelection(selectAllScope.value, select)
}

// Folder-list checkbox: selects every video in the folder (sub-folders included).
// Files are fetched here rather than through the store so the shared
// `Streams/vodFiles` list is left untouched.
const folderFilesCache = ref({})
const folderLoading = ref({})
const folderRequests = new Map()
let isUnmounted = false
onBeforeUnmount(() => { isUnmounted = true })

const selectionSession = () => playlistContentSelection?.session?.value


// Same order the open folder shows (see GET_VOD_FILES and the vodFiles getter)
const sortFolderFiles = (files) => {
	const newestFirst = _.orderBy(files, ['creationTime'], ['desc'])
	return sortStreams(newestFirst, store.state.Streams.sortBy, true) ?? newestFirst
}

const fetchFolderFiles = async (folder, { silent = false } = {}) => {
	folderLoading.value = { ...folderLoading.value, [folder._id]: true }
	try {
		const files = await StreamService.getStreamPlaylist(folder._id)
		return sortFolderFiles(Array.isArray(files) ? files : [])
	} catch (err) {
		console.error('load folder videos failed: ', err)
		if (!silent) notify({ group: 'error', text: `Could not load videos in ${folder.name}` })
		return null
	} finally {
		folderLoading.value = { ...folderLoading.value, [folder._id]: false }
	}
}

const loadFolderFiles = async (folder, options) => {
	const cached = folderFilesCache.value[folder._id]
	if (cached) return cached
	// Share one in-flight request between a checkbox click and opening the folder
	if (!folderRequests.has(folder._id)) {
		const session = selectionSession()
		const request = fetchFolderFiles(folder, options).then((files) => {
			folderRequests.delete(folder._id)
			if (files && session === selectionSession()) {
				folderFilesCache.value = { ...folderFilesCache.value, [folder._id]: files }
			}
			return files
		})
		folderRequests.set(folder._id, request)
	}
	return folderRequests.get(folder._id)
}

const folderState = (folder) => {
	const files = folderFilesCache.value[folder._id]
	if (!hasBulkSelection.value || !files) return 'none'
	return playlistContentSelection.getState(files, 'vod')
}

const toggleFolder = async (folder, select) => {
	if (!hasBulkSelection.value) return
	const session = selectionSession()
	const files = await loadFolderFiles(folder)
	// The modal was closed or reopened while loading: that click no longer applies
	if (isUnmounted || session !== selectionSession()) return
	applyBulkSelection(files, select)
}

// Folder-list "Select all": every video in every listed folder (search applied,
// all pages). Folders that haven't been loaded yet count as unselected.
const allFoldersLoading = computed(() => Object.values(folderLoading.value).some(Boolean))

const allFoldersState = computed(() => {
	const folders = vodFoldersFiltered.value
	if (!hasBulkSelection.value || !folders.length) return 'none'
	const states = folders.map((folder) => folderState(folder))
	if (states.every((state) => state === 'all')) return 'all'
	return states.some((state) => state !== 'none') ? 'some' : 'none'
})

const toggleAllFolders = async (select) => {
	if (!hasBulkSelection.value) return
	const folders = vodFoldersFiltered.value
	const session = selectionSession()
	const results = await Promise.all(folders.map((folder) => loadFolderFiles(folder, { silent: true })))
	if (isUnmounted || session !== selectionSession()) return
	const failed = results.filter((files) => !files).length
	if (failed) {
		notify({ group: 'error', text: `Could not load videos in ${failed} folder${failed > 1 ? 's' : ''}` })
	}
	applyBulkSelection(_.uniqBy(results.flatMap((files) => files || []), 'id'), select)
}

// Each modal open starts from fresh data (the mobile sheet stays mounted). When the
// saved playlist already has VODs, load every folder's videos so folder rows and
// "Select all" show the saved selection straight away instead of looking empty.
let cacheSession
const selectableFolders = computed(() => vodStreams.value.filter((stream) => stream.totalFiles > 0))
watch(
	[selectionSession, selectableFolders],
	([session]) => {
		if (session !== cacheSession) {
			cacheSession = session
			folderFilesCache.value = {}
			folderRequests.clear()
		}
		if (!hasBulkSelection.value || !playlistContentSelection.hasSavedVods?.value) return
		selectableFolders.value.forEach((folder) => loadFolderFiles(folder, { silent: true }))
	},
	{ immediate: true },
)

const shouldShowFilesInFolder = (folder) => folder?._id === activeFolder.value?._id

// Paywall: a sub-folder can be selected as a source in one click, like a whole
// folder — emit the parent folder tagged with the sub-folder so the paywall
// scopes its player URL to it.
const addSubfolderAsSource = (subfolderRow) => {
	const folder = activeFolder.value
	if (!folder) return
	emit('add-vod', {
		...folder,
		name: `${folder.name} / ${subfolderRow.name}`,
		subfolder: { _id: subfolderRow._id, name: subfolderRow.name },
	})
}

const addFile = (file, folder) => {
	// Sites needs the folder too: its castr ref is { vod folder id, file id }.
	if (props.source === 'dynamic' || props.source === 'mediaflow' || props.source === 'sites') {
		emit('add-vod', { file, folder })
	} else {
		emit('add-vod', file)
	}
}

// Row-level selection: dynamic players toggle their radio, Sites picks the
// file outright (the "+" button stays as the visible affordance; clicks that
// land on it are left to its own handler so the file is not added twice).
const rowSelectsFile = computed(() => props.source === 'sites')
const onFileRow = (file, folder, event) => {
	if (disabledMessage(file, folder)) return
	if (props.source === 'dynamic') return toggleStream(file, folder)
	if (!rowSelectsFile.value) return
	if (event?.target instanceof Element && event.target.closest('button, input, a')) return
	addFile(file, folder)
}

const disabledMessage = (vodFile, vodFolder) => {
	const isAllowedCodecPlayout =
		(props.source === 'playout' &&
			vodFile.mediaInfo?.codecs?.find((element) => element.codec === 'AVC')) ||
		vodFolder?.liveParent ||
		props.source !== 'playout'
	const isExceedFileSizePlayout =
		props.source === 'playout' && vodFile.bytes > 10737418240
	const isExceedFileResolutionPlayout =
		props.source === 'playout' &&
		(vodFile.mediaInfo?.width > 1920 || vodFile.mediaInfo?.height > 1080)

	if (isExceedFileResolutionPlayout) {
		return 'Unsupported resolution — this feature only allows videos up to 1080p.'
	}
	if (!isAllowedCodecPlayout) {
		return 'This video cannot be added because it is not encoded in the H.264 codec. Please re-encode the video using the H.264 format and upload it again to Video Hosting.'
	}
	if (isExceedFileSizePlayout) {
		return 'This file exceeds 10 GB and cannot be added as content in TV Playout.'
	}
	if (props.source === 'mediaflow' && vodFile?.bytes > 2 * 1024 * 1024 * 1024) {
		return 'This file exceeds the 2GB size limit, so cannot be added.'
	}
	if (
		props.source === 'mediaflow' &&
		vodFile?.mediaInfo?.height >= 1920 &&
		vodFile?.mediaInfo?.width >= 1080
	) {
		return 'This file exceeds the 1920x1080 resolution limit, so cannot be added.'
	}

	return false
}

const isChecked = (file) => {
	const id = file?._id || file?.id || file?.refId
	if (props.source === 'playlist' && playlistContentSelection) {
		return playlistContentSelection.isSelected(file, 'vod')
	}
	if (props.source === 'dynamic') {
		return selectedVod.value === id
	}
	return false
}

const toggleStream = (stream, folder = null) => {
	if (props.source === 'playlist' && playlistContentSelection) {
		playlistContentSelection.toggle(stream, 'vod')
		return
	}
	if (props.source === 'playlist') {
		emit('add-vod', stream)
		return
	}
	if (props.source === 'dynamic') {
		selectedVod.value = stream?._id || stream?.id || stream?.refId
		if (folder) {
			emit('add-vod', { file: stream, folder })
		} else {
			emit('add-vod', stream)
		}
	}
}
</script>

<template>
	<section v-if="!hasVodFiles" class="min-h-28">
		<h3 class="text-sm text-surface-8 font-medium text-center mt-5">
			You have no videos uploaded at Video Hosting
		</h3>
		<p class="text-center my-3">
			<FormButton to="/vods">
				Open Video Hosting
			</FormButton>
		</p>
	</section>
	<section v-else>
		<div class="flex items-center mb-3 relative" :class="{ 'mt-3': multiview || props.source === 'mediaflow' }">
			<icon-base class="absolute z-10 left-2 text-surface-7">
				<icon-search />
			</icon-base>
			<FormInput :placeholder="searchPlaceholder" customClasses="!px-8" v-model="search" />
			<FormButton class="absolute z-10 right-0" isIcon type="link" v-if="search" @click="clearSearch">
				<icon-base>
					<icon-cross />
				</icon-base>
			</FormButton>
		</div>
		<slot />
		<div v-if="hasBulkSelection && !isFolderOpen && !showSearchEmptyState && vodFoldersFiltered.length > 0" class="mb-3">
			<FormCheckbox :key="`vod-select-all-folders-${bulkCheckboxKey}`" name="vod-select-all-folders" :input-value="true"
				:label="allFoldersState === 'all' ? 'Unselect all' : 'Select all'"
				:modelValue="allFoldersState === 'all'" :indeterminate="allFoldersState === 'some'"
				:disabled="allFoldersLoading" @update:modelValue="toggleAllFolders" />
		</div>
		<section v-if="showSearchEmptyState" class="min-h-48 text-center flex flex-col items-center justify-center gap-y-2">
			<icon-base width="64" height="64" widthViewBox="64" heightViewBox="64">
				<icon-search-empty />
			</icon-base>
			<p class="text-sm text-surface-8 font-medium">No results found</p>
			<p class="text-xs text-surface-6">Try adjusting your search to find what you’re looking for</p>
		</section>
		<PaginatedList
			v-else-if="foldersForList.length > 0"
			:items="foldersForList"
			:page-size="itemsPerPage"
			:source="source"
			:page-size-options="itemsPerPageOptions"
			:show-page-size-selector="showItemsPerPageSelector"
			:list-class="listClasses"
			item-key="_id"
		>
			<template #default="{ item: folder }">
				<!-- Modern variant: clean card rows -->
				<li v-if="props.variant === 'modern'" :title="folder.name"
					:class="activeFolder && !shouldShowFilesInFolder(folder) ? 'hidden' : ''">
					<!-- Checkbox and drill-in button are siblings: a checkbox can't live inside a <button> -->
					<div v-if="!shouldShowFilesInFolder(folder)"
						class="group flex items-center rounded-lg bg-surface-3 hover:bg-surface-4 ring-1 ring-inset ring-white/[0.05] transition-colors">
						<FormCheckbox v-if="hasBulkSelection" class="shrink-0 pl-2.5" :key="`vod-folder-${folder._id}-${bulkCheckboxKey}`" :name="`vod-folder-${folder._id}`"
							:input-value="folder._id" :modelValue="folderState(folder) === 'all'"
							:indeterminate="folderState(folder) === 'some'" :disabled="!!folderLoading[folder._id]"
							@update:modelValue="(checked) => toggleFolder(folder, checked)" />
						<FormButton @click="getFiles(folder)"
							custom-classes="min-w-0 w-full flex items-center gap-3 p-2.5 text-left">
							<span class="shrink-0 grid place-items-center size-9 rounded-md bg-surface-4 text-surface-7 group-hover:text-white transition-colors">
								<icon-base fill="none">
									<icon-folder-filled />
								</icon-base>
							</span>
							<div class="min-w-0 flex-1">
								<div class="flex items-center gap-2">
									<badge v-if="folder.liveParent" class="shrink-0 text-nowrap" title="Live-to-VOD" />
									<span class="truncate text-sm font-medium text-surface-text-1">{{ folder.name }}</span>
								</div>
								<p class="text-xs text-surface-6 mb-0 mt-0.5">{{ folder.totalFiles }} {{ folder.totalFiles === 1 ? 'video' : 'videos' }}</p>
							</div>
							<icon-base class="shrink-0 text-surface-6 group-hover:text-white transition-colors" fill="none">
								<icon-chevron-right />
							</icon-base>
						</FormButton>
					</div>
					<template v-else>
						<div class="flex items-center justify-between gap-3 mb-2">
							<FormButton @click="getFiles(folder)"
								custom-classes="group min-w-0 inline-flex items-center gap-1.5 -ml-1 pr-2 py-1 rounded-md text-surface-8 hover:text-white transition-colors">
								<icon-base class="shrink-0" fill="none">
									<icon-chevron-left />
								</icon-base>
								<badge v-if="folder.liveParent" class="shrink-0 text-nowrap" title="Live-to-VOD" />
								<span class="truncate text-sm font-medium">{{ folder.name }}</span>
								<template v-if="activeSubfolder">
									<span class="text-surface-6">/</span>
									<span class="truncate text-sm font-medium text-white">{{ activeSubfolder.name }}</span>
								</template>
							</FormButton>
							<FormCheckbox v-if="selectAllScope.length" class="shrink-0" :key="`vod-select-all-${bulkCheckboxKey}`" name="vod-select-all" :input-value="true"
								:label="selectAllState === 'all' ? 'Unselect all' : 'Select all'"
								:modelValue="selectAllState === 'all'" :indeterminate="selectAllState === 'some'"
								@update:modelValue="toggleSelectAll" />
						</div>
						<Spinner v-if="loadFiles" class="my-2" />
						<PaginatedList v-else-if="folderContentItems.length > 0" :key="folder._id + (activeSubfolder?._id || '')"
							:items="folderContentItems" :source="source" :page-size="itemsPerPage"
							:page-size-options="itemsPerPageOptions" :show-page-size-selector="showItemsPerPageSelector"
							:list-class="subListClasses" item-key="_id">
							<template #default="{ item: file }">
								<!-- Sub-folder row: drill one level deeper; in checkbox mode it can
								     also be selected as a unit (= all of its videos) -->
								<li v-if="file.__subfolder" :title="file.name"
									class="group flex items-center rounded-lg bg-surface-3 hover:bg-surface-4 ring-1 ring-inset ring-white/[0.05] transition-colors">
									<FormCheckbox v-if="hasBulkSelection" class="shrink-0 pl-2.5" :key="`vod-subfolder-${file._id}-${bulkCheckboxKey}`" :name="`vod-subfolder-${file._id}`"
										:input-value="file._id" :modelValue="subfolderState(file) === 'all'"
										:indeterminate="subfolderState(file) === 'some'" @update:modelValue="(checked) => toggleSubfolder(file, checked)" />
									<button type="button" @click="openSubfolder(file)"
										class="min-w-0 w-full flex items-center gap-3 p-2.5 text-left">
										<span class="shrink-0 grid place-items-center size-9 rounded-md bg-surface-4 text-surface-7 group-hover:text-white transition-colors">
											<icon-base fill="none">
												<icon-folder-filled />
											</icon-base>
										</span>
										<div class="min-w-0 flex-1">
											<p class="truncate text-sm font-medium text-surface-text-1 mb-0">{{ file.name }}</p>
											<p class="text-xs text-surface-6 mb-0 mt-0.5">{{ file.totalFiles }} {{ file.totalFiles === 1 ? 'video' : 'videos' }}</p>
										</div>
										<icon-base class="shrink-0 text-surface-6 group-hover:text-white transition-colors" fill="none">
											<icon-chevron-right />
										</icon-base>
									</button>
								</li>
								<li v-else :title="file.fileName"
									class="group relative flex items-center gap-3 p-2.5 rounded-lg bg-surface-3 hover:bg-surface-4 ring-1 ring-inset ring-transparent cursor-pointer transition-colors has-[input:checked]:!ring-main has-[input:checked]:!bg-main-transparent"
									:role="rowSelectsFile ? 'button' : undefined" :tabindex="rowSelectsFile && !disabledMessage(file, folder) ? 0 : undefined"
									:data-testid="rowSelectsFile ? `vod-file-row-${file._id}` : undefined"
									@click="onFileRow(file, folder, $event)" @keydown.enter.self.prevent="onFileRow(file, folder)" @keydown.space.self.prevent="onFileRow(file, folder)">
									<FormRadio v-if="shouldShowRadio" class="sr-only absolute opacity-0 pointer-events-none w-0 h-0"
										name="dynamic-vod-source" :modelValue="selectedVod" :inputValue="file._id"
										@update:modelValue="toggleStream(file, folder)" />
									<FormCheckbox v-if="shouldShowCheckbox" class="shrink-0" :modelValue="isChecked(file)"
										@change="toggleStream(file)" />
									<span class="shrink-0 grid place-items-center size-9 rounded-md bg-surface-4 text-surface-7 group-hover:text-white transition-colors">
										<icon-base fill="none">
											<icon-film />
										</icon-base>
									</span>
									<Tooltip wrapperClass="min-w-0 flex-1" :active="!!disabledMessage(file, folder)"
										:content="disabledMessage(file, folder)">
										<div class="min-w-0">
											<p class="truncate text-sm font-medium text-surface-text-1 mb-0">{{ file.fileName }}</p>
											<p class="text-xs text-surface-6 mb-0 mt-0.5">{{ bytes(file.bytes) }} · {{ duration(file.mediaInfo?.duration * 1000) }}</p>
										</div>
									</Tooltip>
								</li>
							</template>
						</PaginatedList>
						<p v-else class="text-sm text-surface-7 text-center py-6">No videos in this folder.</p>
					</template>
				</li>

				<!-- Default variant -->
				<li v-else :title="folder.name" :class="folderItemClasses(folder)">
					<div class="flex items-center gap-x-3">
						<FormCheckbox v-if="hasBulkSelection" class="shrink-0" :key="`vod-folder-${folder._id}-${bulkCheckboxKey}`" :name="`vod-folder-${folder._id}`"
							:input-value="folder._id" :modelValue="folderState(folder) === 'all'"
							:indeterminate="folderState(folder) === 'some'" :disabled="!!folderLoading[folder._id]"
							@update:modelValue="(checked) => toggleFolder(folder, checked)" />
						<FormButton
							customClasses="h-8 text-sm flex items-center gap-x-2 hover:text-white w-full max-w-[calc(100%-3rem)]"
							@click="getFiles(folder)">
							<icon-base class="transition-transform shrink-0"
								:class="shouldShowFilesInFolder(folder) ? 'rotate-90' : 'rotate-0'">
								<icon-chevron-right-filled />
							</icon-base>
							<badge v-if="folder.liveParent" class="text-nowrap" title="Live-to-VOD" />
							<span class="min-w-0 grow-0 max-w-full truncate text-sm shrink">{{ folder.name }}</span>
						</FormButton>
						<FormButton v-if="props.source === 'paywall' && !folder.liveParent" size="sm" isIcon
							class="shrink-0 ml-auto" @click="addFile(folder)">
							<icon-base>
								<icon-plus />
							</icon-base>
						</FormButton>
					</div>
					<Spinner v-if="loadFiles && shouldShowFilesInFolder(folder)" class="my-2" />
					<div v-if="shouldShowFilesInFolder(folder) && selectAllScope.length" class="flex justify-end mt-2">
						<FormCheckbox :key="`vod-select-all-${bulkCheckboxKey}`" name="vod-select-all" :input-value="true"
							:label="selectAllState === 'all' ? 'Unselect all' : 'Select all'"
							:modelValue="selectAllState === 'all'" :indeterminate="selectAllState === 'some'"
							@update:modelValue="toggleSelectAll" />
					</div>
					<PaginatedList
						v-if="!loadFiles && shouldShowFilesInFolder(folder) && folderContentItems.length > 0"
						:key="folder._id + (activeSubfolder?._id || '')"
						:items="folderContentItems"
						:source="source" :page-size="itemsPerPage" :page-size-options="itemsPerPageOptions"
						:show-page-size-selector="showItemsPerPageSelector" :list-class="subListClasses" class="!mt-2"
						item-key="_id">
						<template #default="{ item: file }">
							<!-- Sub-folder row: drill one level deeper; selectable as a unit in
							     checkbox mode -->
							<li v-if="file.__subfolder" :title="file.name" :class="subListItemClasses">
								<FormCheckbox v-if="hasBulkSelection" class="shrink-0" @click.stop
									:key="`vod-subfolder-${file._id}-${bulkCheckboxKey}`" :name="`vod-subfolder-${file._id}`" :input-value="file._id"
									:modelValue="subfolderState(file) === 'all'" :indeterminate="subfolderState(file) === 'some'"
									@update:modelValue="(checked) => toggleSubfolder(file, checked)" />
								<FormButton
									:customClasses="`h-8 text-sm flex items-center gap-x-2 hover:text-white w-full ${props.source === 'paywall' ? 'max-w-[calc(100%-3rem)]' : ''}`"
									@click="openSubfolder(file)">
									<icon-base class="shrink-0">
										<icon-chevron-right-filled />
									</icon-base>
									<icon-base class="shrink-0" fill="none">
										<icon-folder-filled />
									</icon-base>
									<span class="min-w-0 grow-0 max-w-full truncate text-sm shrink">{{ file.name }}</span>
									<span class="text-xs text-surface-6 shrink-0 ml-auto">{{ file.totalFiles }}</span>
								</FormButton>
								<FormButton v-if="props.source === 'paywall'" size="sm" isIcon class="shrink-0 ml-auto"
									@click="addSubfolderAsSource(file)">
									<icon-base>
										<icon-plus />
									</icon-base>
								</FormButton>
							</li>
							<li v-else :title="file.fileName" :class="[subListItemClasses, rowSelectsFile && !disabledMessage(file, folder) ? 'cursor-pointer rounded hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-main' : '']"
								:role="rowSelectsFile ? 'button' : undefined" :tabindex="rowSelectsFile && !disabledMessage(file, folder) ? 0 : undefined"
								:data-testid="rowSelectsFile ? `vod-file-row-${file._id}` : undefined"
								@click="onFileRow(file, folder, $event)" @keydown.enter.self.prevent="onFileRow(file, folder)" @keydown.space.self.prevent="onFileRow(file, folder)">
								<FormCheckbox v-if="shouldShowCheckbox" :modelValue="isChecked(file)" @change="toggleStream(file)" />
								<FormRadio v-if="shouldShowRadio" class="sr-only absolute opacity-0 pointer-events-none w-0 h-0"
									name="dynamic-vod-source"
									:modelValue="selectedVod"
									:inputValue="file._id"
									@update:modelValue="toggleStream(file, folder)" />
								<Tooltip wrapperClass="w-full min-w-0" :active="!!disabledMessage(file, folder)"
									:content="disabledMessage(file, folder)">
									<div class="flex gap-x-2 items-center w-full py-1 px-2">
										<icon-base class="shrink-0">
											<icon-film />
										</icon-base>
										<span class="text-sm grow-0 truncate">{{ file.fileName }}</span>
										<FormButton v-if="shouldShowAddVodFileButon()" size="sm" isIcon class="shrink-0 ml-auto"
											:disabled="!!disabledMessage(file, folder)" @click="addFile(file, folder)">
											<icon-base>
												<icon-plus />
											</icon-base>
										</FormButton>
									</div>
								</Tooltip>
							</li>
						</template>
					</PaginatedList>
				</li>
			</template>
		</PaginatedList>
	</section>
</template>

<style scoped></style>
